import { useState, useEffect } from 'react';

/**
 * Custom hook for managing user filters and search functionality
 *
 * @returns {Object} Filter states and setters
 * @returns {string} returns.searchQuery - Current search query
 * @returns {Function} returns.setSearchQuery - Setter for search query
 * @returns {string} returns.debouncedSearch - Debounced search query (500ms delay)
 * @returns {string} returns.selectedRole - Currently selected role filter
 * @returns {Function} returns.setSelectedRole - Setter for role filter
 * @returns {string} returns.selectedStatus - Currently selected status filter
 * @returns {Function} returns.setSelectedStatus - Setter for status filter
 * @returns {string} returns.sortBy - Current sort option
 * @returns {Function} returns.setSortBy - Setter for sort option
 * @returns {number} returns.currentPage - Current page number
 * @returns {Function} returns.setCurrentPage - Setter for current page
 * @returns {string} returns.viewMode - Current view mode ('cards' or 'table')
 * @returns {Function} returns.setViewMode - Setter for view mode
 *
 * @example
 * const {
 *   searchQuery,
 *   setSearchQuery,
 *   debouncedSearch,
 *   selectedRole,
 *   setSelectedRole,
 *   selectedStatus,
 *   setSelectedStatus,
 *   sortBy,
 *   setSortBy,
 *   currentPage,
 *   setCurrentPage,
 *   viewMode,
 *   setViewMode
 * } = useUserFilters();
 */
const useUserFilters = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filter states
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // View mode state
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  /**
   * Debounce search query
   * Updates debouncedSearch 500ms after user stops typing
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return {
    // Search
    searchQuery,
    setSearchQuery,
    debouncedSearch,

    // Filters
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    sortBy,
    setSortBy,

    // Pagination
    currentPage,
    setCurrentPage,

    // View mode
    viewMode,
    setViewMode
  };
};

export default useUserFilters;
